import { forwardRef, useRef } from "react";

import { composeRefs } from "@radix-ui/react-compose-refs";

import * as Control from "../Control/control.component";
import { controlInputVariants } from "../Control/control.variant";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * TextField
 * -----------------------------------------------------------------------------------------------*/

interface TextFieldProps extends React.ComponentPropsWithRef<"input"> {
  defaultValue?: string | number;
  value?: string | number;
  type?:
    | "date"
    | "datetime-local"
    | "email"
    | "hidden"
    | "month"
    | "number"
    | "password"
    | "search"
    | "tel"
    | "text"
    | "time"
    | "url"
    | "week";
  error?: boolean;
  rounded?: boolean;
}

const Root = forwardRef<HTMLInputElement, TextFieldProps>((props, forwardedRef) => {
  const { className, style, error, rounded = false, children, onChange: onChangeProp, disabled, ...inputProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const composedRefs = composeRefs(inputRef, forwardedRef);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("input, button, a")) return;

    const input = inputRef.current;
    if (!input) return;

    const isRightSlot = target.closest(
      "[data-control-slot][data-side='right'], [data-control-slot]:not([data-side='right']) ~ [data-control-slot]:not([data-side='left'])",
    );

    const cursorPosition = isRightSlot ? input.value.length : 0;

    requestAnimationFrame(() => {
      // Only some input types support this, browsers will throw an error if not supported
      // See: https://developer.mozilla.org/en-us/docs/Web/API/HTMLInputElement/setSelectionRange#:~:text=Note%20that%20according,not%20support%20selection%22.
      try {
        input.setSelectionRange(cursorPosition, cursorPosition);
        // oxlint-disable-next-line no-unused-vars
      } catch (e) {
        /* empty */
      }
      input.focus();
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => !disabled && onChangeProp?.(event);

  return (
    <Control.Root className={className} style={style} error={error} rounded={rounded} onPointerDown={onPointerDown}>
      <input
        spellCheck={false}
        {...inputProps}
        ref={composedRefs}
        className={cn(controlInputVariants())}
        onChange={onChange}
        disabled={disabled}
      />
      {children}
    </Control.Root>
  );
});

Root.displayName = "TextField";

export { Slot } from "../Control/control.component";
export type { SlotProps } from "../Control/control.component";
export { Root };
export type { TextFieldProps };
